import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layers, Loader2, Camera, Trash2, Plus } from 'lucide-react';
import { api, fileUrl } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import { useShell } from '../components/AppShell';
import FirstRun from '../components/FirstRun';

// "3 days ago" is a fact somebody can use; a timestamp is one they have to
// decode.
function since(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  if (s < 2592000) return `${Math.round(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // The catalogue modals and the Create menu live in the shell now, so this
  // page can open them without owning them.
  const { openCreate, openPhotoSessions, openUgcVideos, videoOn } = useShell();
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  // Whether this account can actually render, asked of the server rather than
  // guessed from whether a key string is non-empty: one Nexaxis token covers one
  // capability, so having "a key" and being able to make an image are different
  // facts. null while unknown, so nothing flashes on screen before we know.
  const [ready, setReady] = useState(null);
  const [imageProviders, setImageProviders] = useState([]);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setWorkflows(await api.listWorkflows());
      } catch (e) {
        // A network failure must not masquerade as an empty account.
        toast({ title: 'Could not load your sessions', description: 'Check your connection and reload.' });
      }
      try {
        const k = await api.myKeys();
        setReady(k.ready || {});
        setImageProviders((k.providers?.image?.providers) || []);
        if (k.onboarding_done) setDismissed(true);
      } catch (e) { /* the panel below simply will not offer itself */ }
      setLoading(false);
    })();
  }, []);

  const [deletingId, setDeletingId] = useState(null);

  const removeWorkflow = async (w) => {
    // Named in the question, because "are you sure" beside three identical
    // cards is not enough to know which one is about to go.
    //
    // Y decia que las fotos se iban con ella, que es falso: el endpoint las
    // conserva a proposito -- "what it made stays" -- y el propio aviso de
    // despues contaba cuantas se habian quedado. Un dialogo que amenaza con
    // borrar lo que no borra hace que nadie pulse, y esa es exactamente la
    // queja: no se podian borrar las sesiones.
    const ok = window.confirm(
      `Delete the session "${w.name}"?\n\n`
      + 'The photographs it made are kept — they stay in Photos & videos. '
      + 'Only the canvas goes.');
    if (!ok) return;
    setDeletingId(w.id);
    try {
      const r = await api.deleteWorkflow(w.id);
      setWorkflows((prev) => prev.filter((x) => x.id !== w.id));
      toast({
        title: 'Session deleted',
        description: r.kept ? `${r.kept} photograph${r.kept === 1 ? '' : 's'} kept in Photos & videos.`
                            : `${w.name} had made nothing yet.`,
      });
    } catch (e) {
      toast({ title: 'Could not delete it', description: e.message });
    } finally {
      setDeletingId(null);
    }
  };

  // Name and recipe, because those are the two things somebody remembers.
  // Sorted by last touch, newest first, on a copy: the state itself is not
  // ours to reorder.
  const needle = q.trim().toLowerCase();
  const byRecent = [...workflows]
    .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  const visible = byRecent
    .filter((w) => !needle
      || `${w.name || ''} ${w.recipe_label || ''}`.toLowerCase().includes(needle));
  // Whatever was touched last is what somebody almost certainly came back for.
  const recent = byRecent.slice(0, 3);

  return (
    <main className="mx-auto max-w-6xl px-6 py-10">
      {/* Above the greeting, because a studio with no key attached cannot take
          a photograph and every other control here leads somewhere that ends
          in that discovery. Shown while images are not renderable and the
          person has not waved it away; once a key answers it never returns. */}
      {ready && !ready.image && !dismissed && imageProviders.length > 0 && (
        <div className="mb-8">
          <FirstRun
            providers={imageProviders}
            onDone={() => { setReady((r) => ({ ...(r || {}), image: true })); setDismissed(true); }}
            onSkip={() => setDismissed(true)}
          />
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif-display text-3xl">Welcome back, {user?.name?.split(' ')[0] || 'Director'}</h1>
          <p className="mt-1 text-sm text-white/50">Upload your product or your face. Pick a session. Collect the photographs.</p>
        </div>
        {workflows.length > 0 && (
          <button
            onClick={openCreate}
            data-testid="home-create"
            className="inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full bg-white px-5 text-sm font-medium text-black transition-colors hover:bg-white/90"
          >
            <Plus size={15} /> Create something new
          </button>
        )}
      </div>

      {/* Continue where you left off — the three freshest sessions as wide
          rows, so coming back to yesterday's work is one glance and one
          click, not a scan of the whole grid. */}
      {!loading && recent.length > 0 && (
        <div className="mt-10">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-white/50">Continue where you left off</h2>
          <div className="mt-4 grid gap-3 lg:grid-cols-3">
            {recent.map((w) => (
              <button
                key={w.id}
                onClick={() => navigate(`/app/studio/${w.id}`)}
                data-testid={`continue-${w.id}`}
                className="flex items-center gap-4 rounded-2xl border border-[#2A2A30] bg-[#141417] p-4 text-left transition-colors hover:border-white/25"
              >
                <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-[#2A2A30] bg-[#0E0E10]">
                  {w.thumb_url ? (
                    <img src={fileUrl(w.thumb_url)} alt="" loading="lazy" className="h-full w-full object-cover" />
                  ) : (
                    <Layers size={18} className="text-white/25" />
                  )}
                </div>
                <div className="min-w-0">
                  <p className="truncate font-medium">{w.name}</p>
                  <p className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-xs text-white/40">
                    {w.recipe_label && <span className="truncate text-white/60">{w.recipe_label}</span>}
                    {w.recipe_label && w.updated_at && <span className="text-white/20">·</span>}
                    {w.updated_at && <span>{since(w.updated_at)}</span>}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* All sessions */}
      {/* A name and a piece count is a list of forty five things called
          Untitled. What tells them apart is the recipe they came from, when
          they were last touched, and being able to type three letters. */}
      <div className="mt-12 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-white/50">
          All sessions {workflows.length > 0 && <span className="text-white/25">· {workflows.length}</span>}
        </h2>
        {workflows.length > 0 && (
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="session-search"
            placeholder="Search your sessions…"
            className="h-9 w-full max-w-xs rounded-full border border-[#2A2A30] bg-[#141417] px-4 text-sm text-white placeholder:text-white/25 focus:border-white/40 focus:outline-none sm:w-64"
          />
        )}
      </div>
      {loading ? (
        <div className="mt-6 flex justify-center py-10"><Loader2 className="animate-spin text-white/40" /></div>
      ) : workflows.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-[#2A2A30] p-10 text-center">
          <p className="text-white/50">Nothing here yet.</p>
          {/* First run is the worst moment to hand somebody an empty grid, and
              for a long time this pointed at the video scenarios. The product
              is photographs, so this is where it starts. */}
          <button onClick={openPhotoSessions} data-testid="empty-start-session" className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2 text-sm font-medium text-black">
            <Camera size={15} /> Start from a photo session
          </button>
          {videoOn && (
            <button onClick={openUgcVideos} className="mt-2 block w-full text-xs text-white/40 hover:text-white/70">
              or make a UGC video instead
            </button>
          )}
        </div>
      ) : (
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((w) => (
            <div key={w.id} className="group relative rounded-2xl border border-[#2A2A30] bg-[#141417] transition-colors hover:border-white/25">
              <button onClick={() => navigate(`/app/studio/${w.id}`)} className="w-full p-5 text-left">
                <div className="flex h-24 items-center justify-center overflow-hidden rounded-lg border border-[#2A2A30] bg-[#0E0E10]">
                  {w.thumb_url ? (
                    <img src={fileUrl(w.thumb_url)} alt="" loading="lazy"
                         className="h-full w-full object-cover" />
                  ) : (
                    <Layers size={22} className="text-white/25" />
                  )}
                </div>
                <p className="mt-4 truncate pr-7 font-medium">{w.name}</p>
                <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-white/40">
                  {w.recipe_label && (
                    <span className="truncate text-white/60">{w.recipe_label}</span>
                  )}
                  {w.recipe_label && <span className="text-white/20">·</span>}
                  <span>{(w.graph_json?.nodes || []).length} shots</span>
                  {w.updated_at && <span className="text-white/20">·</span>}
                  {w.updated_at && <span>{since(w.updated_at)}</span>}
                </p>
              </button>
              {/* Estaba en opacity-0 hasta el hover, para que borrar no fuera
                  lo mas facil de acertar. En una tableta no hay hover, asi que
                  lo que hacia era no existir: en tactil no habia forma de
                  borrar una sesion. Se queda visible pero apagado, y solo se
                  enciende en rojo al apuntarlo. */}
              <button
                onClick={() => removeWorkflow(w)}
                disabled={deletingId === w.id}
                data-testid={`workflow-delete-${w.id}`}
                title="Delete this session"
                aria-label={`Delete ${w.name}`}
                className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-md text-white/35 transition-colors hover:bg-red-500/15 hover:text-red-300 focus:text-red-300 focus:outline-none focus:ring-1 focus:ring-red-400/50 disabled:opacity-50"
              >
                {deletingId === w.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              </button>
            </div>
          ))}
        </div>
      )}
    </main>
  );
}
